/**
 * useIntegrityMonitor — client-side camera/mic integrity checks for the
 * proctored interview, running entirely in the browser (no frames leave the
 * device except the single event type + score that gets reported).
 *
 * Detections:
 *  - multiple_persons / no_person / looking_away — MediaPipe Face Detector
 *    (face count; a face whose bounding box sits well off-center for a
 *    sample is treated as "looking away" — a rough proxy, not real gaze
 *    tracking, since that needs iris landmarks this lightweight model
 *    doesn't provide).
 *  - electronic_device — TensorFlow.js COCO-SSD object detector, flags a
 *    cell phone / laptop / book / remote in frame.
 *  - camera_blocked — no model needed: near-zero brightness variance across
 *    a downsampled frame (a covered/black lens looks flat and dark).
 *  - multiple_audio_sources — no model needed either: spectral flatness of
 *    the mic input. A single voice's spectrum is dominated by its
 *    fundamental + harmonics (low flatness); overlapping voices/background
 *    conversation broadens it toward noise-like (high flatness). This is a
 *    heuristic approximation, not real speaker diarization.
 *
 * Both are open-source, pre-trained models (Apache-2.0 — MediaPipe's
 * BlazeFace and TF.js's COCO-SSD/MobileNet), self-hosted from this app's
 * own /public dir rather than fetched from Google's CDN at request time —
 * see the path constants below for exactly what's served from where and
 * why. The JS runtimes are still lazy/dynamically imported on first
 * activation so their weight is never paid by candidates on non-ML-gated
 * pages. Every trigger runs on the same POLL_INTERVAL_MS cadence below
 * (1s by default) and is then throttled per-type by EVENT_THROTTLE_MS
 * (5s) so a sustained condition doesn't spam the event log — but that
 * throttle never delays the FIRST detection of a new episode, which
 * always reports immediately (straight to the backend, no batching/queue
 * anywhere in this pipeline — "buffering" isn't what creates the gap
 * here, sampling rate is).
 *
 * All tunables live in INTEGRITY_MONITOR_CONFIG below — nothing else in
 * this file needs to change to adjust sensitivity/timing.
 */
import { useEffect, useRef, useState } from 'react'
import type { IntegrityEventType } from '../services/proctoredInterviewPublicApi'

export interface UseIntegrityMonitorOptions {
  videoEl: HTMLVideoElement | null
  micStream: MediaStream | null
  active: boolean
  // Set true during the pre-interview device-check screen so the (multi-
  // second) ML model download/init happens BEFORE the interview starts,
  // not during it — without this, there's a real window right after
  // question 1 loads where the camera is live but detection hasn't
  // finished spinning up yet. Preloading never runs the polling loop or
  // reports events; only `active` does that.
  preload?: boolean
  onEvent: (type: IntegrityEventType, details?: Record<string, any>) => void
}

export const INTEGRITY_MONITOR_CONFIG = {
  // How often every check below runs (face/object/camera/audio), in ms.
  // This is the hard floor on detection latency — nothing that happens and
  // ends entirely between two ticks can ever be caught, no matter how low
  // EVENT_THROTTLE_MS is. 1000ms is a deliberately tight default (down from
  // an earlier 4000ms) so that window is as small as practical; going much
  // below ~500ms starts costing real CPU on the candidate's machine (both
  // models run every tick) and risks janking the rest of the page — raise
  // this if candidates on lower-end hardware report lag, lower it if you
  // need even tighter coverage and can accept the CPU cost.
  POLL_INTERVAL_MS: 1000,

  // Minimum time between two reports of the SAME event type while the
  // condition keeps being true, in ms. This does NOT delay the FIRST
  // detection of a new episode — that always reports immediately, straight
  // to the backend, no batching. It only stops an already-reported,
  // still-ongoing condition from re-logging every single tick. Kept short
  // (5s, down from 15-20s) so a new/repeated episode of the same type is
  // still caught quickly, while a continuously-true condition doesn't spam
  // one row per second.
  EVENT_THROTTLE_MS: {
    multiple_persons: 5000,
    no_person: 5000,
    looking_away: 5000,
    electronic_device: 5000,
    camera_blocked: 5000,
    multiple_audio_sources: 5000,
  } as Record<IntegrityEventType, number>,

  FACE_DETECTOR: {
    // MediaPipe's own confidence gate for counting something as a face at
    // all — lower = more sensitive (more likely to catch a partially
    // visible/angled face, but also more false positives).
    MIN_DETECTION_CONFIDENCE: 0.5,
    // A single detected face whose bounding-box center falls outside
    // [MARGIN, 1 - MARGIN] of the frame width is flagged as looking_away.
    // 0.28 means the middle 44% of the frame counts as "facing the screen".
    LOOKING_AWAY_CENTER_MARGIN: 0.28,
  },

  OBJECT_DETECTOR: {
    MIN_CONFIDENCE: 0.6,
    CLASSES: ['cell phone', 'laptop', 'book', 'remote'] as string[],
  },

  CAMERA_BLOCKED: {
    // Downsampled-frame luminance variance/mean below these = "flat and
    // dark", i.e. a covered or fully obstructed lens.
    MAX_VARIANCE: 8,
    MAX_MEAN_BRIGHTNESS: 25,
  },

  MULTI_AUDIO: {
    // 0-255 frequency-bin average level a sample must clear before the
    // flatness check even runs (silence should never trigger this).
    MIN_LEVEL: 45,
    // Spectral flatness (0..1) above this = noise-like/broadband, the
    // heuristic proxy for overlapping voices.
    MIN_FLATNESS: 0.55,
  },
}

const OBJECT_CLASSES = new Set(INTEGRITY_MONITOR_CONFIG.OBJECT_DETECTOR.CLASSES)

// Both the WASM fileset and the face-detector model are served from our own
// /public dir instead of an external CDN — two reasons: (1) it removes any
// dependency on cdn.jsdelivr.net / storage.googleapis.com being reachable
// from the candidate's network at all, and (2) Google's own documented CDN
// path for this exact model (".../float16/1/blaze_face_short_range.task")
// 404s — the real file at that revision is named ".tflite", not ".task"
// (confirmed directly against the public GCS bucket listing). Silently
// mistaking a 404 for "browser doesn't support this" is exactly why face
// detection looked broken with no useful error surfaced.
//
// WASM files copied from node_modules/@mediapipe/tasks-vision/wasm/ — must
// be re-copied whenever that package is upgraded (version pinned exactly in
// package.json for the same reason: JS wrapper and WASM binary must match).
const MEDIAPIPE_WASM_BASE_PATH = '/mediapipe/wasm'
const FACE_DETECTOR_MODEL_PATH = '/mediapipe/models/blaze_face_short_range.tflite'
// COCO-SSD (lite_mobilenet_v2) weights, likewise self-hosted instead of
// fetched from storage.googleapis.com/tfjs-models/... at request time.
const OBJECT_DETECTOR_MODEL_URL = '/tfjs-models/ssdlite_mobilenet_v2/model.json'

// Each model load pulls down several MB across multiple files (5 shards for the object
// detector alone) - a single transient fetch hiccup on any one of them throws (e.g. tfjs's
// "byte length ... multiple of 4" when a shard comes back short) and would otherwise disable
// that detector for the rest of the interview. Retrying the whole load a couple of times
// absorbs a one-off blip without masking a real, permanent failure (wrong URL, browser
// unsupported, etc.), which will still fail identically on every attempt.
async function withRetry<T>(fn: () => Promise<T>, attempts = 3, delayMs = 800): Promise<T> {
  let lastErr: unknown
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs))
    }
  }
  throw lastErr
}

function spectralFlatness(data: Uint8Array): number {
  let logSum = 0
  let sum = 0
  for (let i = 0; i < data.length; i++) {
    const v = data[i] + 1
    logSum += Math.log(v)
    sum += v
  }
  const geoMean = Math.exp(logSum / data.length)
  const arithMean = sum / data.length
  return arithMean > 0 ? geoMean / arithMean : 0
}

export default function useIntegrityMonitor({
  videoEl, micStream, active, preload = false, onEvent,
}: UseIntegrityMonitorOptions) {
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [modelsError, setModelsError] = useState<string | null>(null)
  const faceDetectorRef = useRef<any>(null)
  const objectDetectorRef = useRef<any>(null)
  const modelsReadyRef = useRef(false)
  const loadingRef = useRef(false)
  const lastEventAtRef = useRef<Record<string, number>>({})
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const detectingObjectRef = useRef(false)
  const lastTimestampRef = useRef(0)

  const emit = (type: IntegrityEventType, details?: Record<string, any>) => {
    const now = Date.now()
    const throttle = INTEGRITY_MONITOR_CONFIG.EVENT_THROTTLE_MS[type] ?? 20000
    if (now - (lastEventAtRef.current[type] || 0) < throttle) return
    lastEventAtRef.current[type] = now
    onEvent(type, details)
  }

  // Lazy-load both ML models once — as soon as EITHER `preload` (the
  // pre-interview device-check screen) or `active` (interview actually
  // started) is true, whichever comes first. Never on page load, before
  // that. Any failure here (CDN blocked, offline, unsupported browser) is
  // logged loudly — a silent failure would otherwise look identical to
  // "nothing detected".
  useEffect(() => {
    if (!(active || preload) || loadingRef.current || modelsReadyRef.current) return
    loadingRef.current = true
    let cancelled = false
    ;(async () => {
      // Face and object detection are loaded independently, each in its own
      // try/catch — a problem with just ONE model must not take the other
      // one down too. Each failure is logged with which model it was,
      // instead of one ambiguous catch-all error. Both models — weights
      // and runtime — are served from OUR OWN /public dir (see the two
      // path constants above), not fetched from Google's CDN at request
      // time: same open-source models (Apache-2.0, from Google's MediaPipe
      // and TF.js model zoos), just bundled with the app instead of
      // depending on an external host being reachable/unblocked.
      const errors: string[] = []

      try {
        const faceDetector = await withRetry(async () => {
          const visionModule = await import('@mediapipe/tasks-vision')
          const vision = await visionModule.FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_BASE_PATH)
          return visionModule.FaceDetector.createFromOptions(vision, {
            baseOptions: { modelAssetPath: FACE_DETECTOR_MODEL_PATH },
            runningMode: 'VIDEO',
            minDetectionConfidence: INTEGRITY_MONITOR_CONFIG.FACE_DETECTOR.MIN_DETECTION_CONFIDENCE,
          })
        })
        if (!cancelled) faceDetectorRef.current = faceDetector
      } catch (err) {
        console.error('Integrity monitor: face detector failed to load — multiple_persons/no_person/looking_away disabled.', err)
        errors.push(`face detector: ${err instanceof Error ? err.message : String(err)}`)
      }

      try {
        const objectDetector = await withRetry(async () => {
          const [cocoSsd] = await Promise.all([import('@tensorflow-models/coco-ssd'), import('@tensorflow/tfjs')])
          return cocoSsd.load({ modelUrl: OBJECT_DETECTOR_MODEL_URL })
        })
        if (!cancelled) objectDetectorRef.current = objectDetector
      } catch (err) {
        console.error('Integrity monitor: object detector failed to load — electronic_device disabled.', err)
        errors.push(`object detector: ${err instanceof Error ? err.message : String(err)}`)
      }

      loadingRef.current = false
      if (cancelled) return
      modelsReadyRef.current = true
      setModelsLoaded(true)
      // camera_blocked and multiple_audio_sources never need either model,
      // so a partial (or total) ML failure is surfaced but not fatal — only
      // note it if at least one detector is actually unusable.
      if (errors.length > 0) setModelsError(errors.join(' | '))
    })()
    return () => { cancelled = true }
  }, [active, preload])

  // Independent AnalyserNode on the raw mic stream for the audio heuristic.
  useEffect(() => {
    if (!active || !micStream) return
    const ctx = new AudioContext()
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    ctx.createMediaStreamSource(micStream).connect(analyser)
    audioContextRef.current = ctx
    analyserRef.current = analyser
    return () => {
      analyserRef.current = null
      audioContextRef.current = null
      ctx.close().catch(() => {})
    }
  }, [active, micStream])

  useEffect(() => {
    if (!active || !videoEl) return
    const interval = setInterval(() => {
      if (!videoEl.videoWidth) return

      if (faceDetectorRef.current) {
        try {
          // MediaPipe requires a strictly increasing integer timestamp per
          // call — guard against two calls landing on the same millisecond.
          const timestamp = Math.max(Math.round(performance.now()), lastTimestampRef.current + 1)
          lastTimestampRef.current = timestamp
          const result = faceDetectorRef.current.detectForVideo(videoEl, timestamp)
          const faces = result?.detections || []
          if (faces.length === 0) {
            emit('no_person')
          } else if (faces.length > 1) {
            emit('multiple_persons', { person_count: faces.length })
          } else {
            const box = faces[0].boundingBox
            if (box) {
              const centerX = (box.originX + box.width / 2) / videoEl.videoWidth
              const margin = INTEGRITY_MONITOR_CONFIG.FACE_DETECTOR.LOOKING_AWAY_CENTER_MARGIN
              if (centerX < margin || centerX > 1 - margin) emit('looking_away')
            }
          }
        } catch (err) {
          console.error('Integrity monitor: face detection call failed.', err)
        }
      }

      const canvas = canvasRef.current || (canvasRef.current = document.createElement('canvas'))
      canvas.width = 64
      canvas.height = 48
      const ctx2d = canvas.getContext('2d')
      if (ctx2d) {
        ctx2d.drawImage(videoEl, 0, 0, canvas.width, canvas.height)
        const frame = ctx2d.getImageData(0, 0, canvas.width, canvas.height).data
        let sum = 0
        let sumSq = 0
        const n = frame.length / 4
        for (let i = 0; i < frame.length; i += 4) {
          const lum = (frame[i] + frame[i + 1] + frame[i + 2]) / 3
          sum += lum
          sumSq += lum * lum
        }
        const mean = sum / n
        const variance = sumSq / n - mean * mean
        const { MAX_VARIANCE, MAX_MEAN_BRIGHTNESS } = INTEGRITY_MONITOR_CONFIG.CAMERA_BLOCKED
        if (variance < MAX_VARIANCE && mean < MAX_MEAN_BRIGHTNESS) emit('camera_blocked')
      }

      if (objectDetectorRef.current && !detectingObjectRef.current) {
        detectingObjectRef.current = true
        objectDetectorRef.current.detect(videoEl)
          .then((predictions: any[]) => {
            const flagged = predictions.find(
              (p) => OBJECT_CLASSES.has(p.class) && p.score > INTEGRITY_MONITOR_CONFIG.OBJECT_DETECTOR.MIN_CONFIDENCE
            )
            if (flagged) emit('electronic_device', { object: flagged.class, confidence: Math.round(flagged.score * 100) / 100 })
          })
          .catch((err: unknown) => console.error('Integrity monitor: object detection call failed.', err))
          .finally(() => { detectingObjectRef.current = false })
      }

      if (analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount)
        analyserRef.current.getByteFrequencyData(data)
        const avg = data.reduce((a, b) => a + b, 0) / data.length
        const { MIN_LEVEL, MIN_FLATNESS } = INTEGRITY_MONITOR_CONFIG.MULTI_AUDIO
        if (avg > MIN_LEVEL && spectralFlatness(data) > MIN_FLATNESS) {
          emit('multiple_audio_sources', { level: Math.round(avg) })
        }
      }
    }, INTEGRITY_MONITOR_CONFIG.POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [active, videoEl])

  return { modelsLoaded, modelsError }
}
